import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { toast } from "../../../core/toast";
import { AssignSessionRequest } from "../../../types";

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result?: T;
  results?: T;
}

export const scheduleApi = createApi({
  reducerPath: "scheduleApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Schedule", "SessionSchedules", "Sessions"],

  endpoints: (builder) => ({
    assignSessionSchedule: builder.mutation<
      ApiResponse<any>,
      AssignSessionRequest
    >({
      query: (data) => ({
        url: `${ApiEndpoints.assignSessionSchedule}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SessionSchedules"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            toast.success(data.message || "Session scheduled successfully!");
          } else {
            toast.error(data.message || "Failed to schedule session");
          }
        } catch (error: any) {
          const errorMessage =
            error?.error?.data?.message ||
            error?.data?.message ||
            "Failed to schedule session. Please try again.";
          toast.error(errorMessage);
        }
      },
    }),
    getSessionSchedules: builder.query<ApiResponse<any>, void>({
      query: () => ({
        url: `${ApiEndpoints.getAllSessionSchedules}`,
        method: "GET",
      }),
      providesTags: ["SessionSchedules"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          const errorMessage =
            error?.error?.data?.message ||
            error?.data?.message ||
            "Failed to load session schedules";
          toast.error(errorMessage);
        }
      },
    }),

    getSessionSchedulesByGym: builder.query<ApiResponse<any>, string>({
      query: (gymId) => ({
        url: `${ApiEndpoints.getSessionScheduleByGymId}/${gymId}`,
        method: "GET",
      }),
      providesTags: (result, error, gymId) => [
        { type: "SessionSchedules", id: gymId },
        "SessionSchedules",
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          const errorMessage =
            error?.error?.data?.message ||
            error?.data?.message ||
            "Failed to load gym session schedules";
          toast.error(errorMessage);
        }
      },
    }),

    deleteSessionSchedule: builder.mutation<ApiResponse<any>, string | number>({
      query: (scheduleId) => ({
        url: `${ApiEndpoints.deleteSessionSchedule}/${scheduleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SessionSchedules"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.success) {
            toast.success(data.message || "Schedule removed");
          } else {
            toast.error(data?.message || "Failed to remove schedule");
          }
        } catch (error: any) {
          const msg =
            error?.error?.data?.message ||
            error?.data?.message ||
            "Failed to remove schedule";
          toast.error(msg);
        }
      },
    }),
  }),
});

export const {
  useAssignSessionScheduleMutation,
  useGetSessionSchedulesQuery,
  useGetSessionSchedulesByGymQuery,
  useDeleteSessionScheduleMutation,
} = scheduleApi;
